import { ContentfulClientApi, createClient, EntryCollection } from 'contentful';
import middleware from './middleware';
import {
  TypeAccountPage,
  TypeKeyValueListFields,
} from '@/core/generated-types';
import {
  CONTENT_TYPE_ACCOUNT_PAGE,
  TypeAccountPageContentGroup,
} from '@/core/constants';
import { filterContentfulObject } from '@/utils/helpers';

export interface GetEntriesConfig {
  contentGroup?: string;
  contentType?: string;
  include?: number;
  locale?: string;
  slug?: string;
  id?: string;
  entryName?: string;
}

export interface KeyValue {
  key: string;
  value: string;
}

export interface InventoryStatusCopy {
  availableCta: string;
  outOfStock: string;
  outOfStockCta: string;
  outOfStockTitle: string;
  outOfStockDescription: string;
  preSale: string;
  preSaleCta: string;
  backOrder: string;
  unavailableTitle: string;
  unavailableDescription: string;
}

export interface ProductVariantCopy {
  [key: string]: string;
}

export interface ProductPageGeneralCopy {
  [key: string]: string;
}

export interface ContentfulPreviewData {
  env?: string;
}

export interface ContentfulLinkExternalFields {
  entryName: string;
  linkText: string;
  ariaLabel: string;
  hasArrow: boolean;
  linkUrl: string;
  blankTarget: boolean;
  noFollow: boolean;
  hasShopSubdomain: boolean;
}

export const client = createClient({
  space: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
  accessToken: process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN || '',
  environment: process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT_ID || 'master',
});

export function previewClient(env: string | null = null): ContentfulClientApi {
  return createClient({
    space: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
    accessToken: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN || '',
    host: 'preview.contentful.com',
    environment: env || 'master',
  });
}

export async function getEntries<T extends object>(
  config: GetEntriesConfig,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
): Promise<EntryCollection<T>> {
  try {
    const contentQuery = {
      'fields.contentGroup': config.contentGroup || '',
      'fields.entryName': config.entryName || '',
      'fields.id': config.id || '',
      'fields.slug': config.slug || '',
      content_type: config.contentType,
      include: config.include || 10,
      locale: config.locale || 'en-US',
    };

    const entries = isPreview
      ? await previewClient(previewData?.env).getEntries<T>(contentQuery)
      : await client.getEntries<T>(contentQuery);

    const sanitizedEntries = middleware(entries);
    return sanitizedEntries;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export function getEntriesByPageType<T extends object>(
  config: GetEntriesConfig,
  pageTypes: object,
  isPreview?: boolean,
  previewData?: ContentfulPreviewData,
) {
  const promises: Promise<EntryCollection<T>>[] = [];

  for (const pageType of Object.values(pageTypes)) {
    promises.push(
      getEntries<T>(
        {
          ...config,
          locale: config.locale ?? '*',
          contentType: pageType,
        },
        isPreview,
        previewData,
      ),
    );
  }

  return Promise.all(promises);
}

export async function getAccountPage(
  targetPageGroup: TypeAccountPageContentGroup,
  locale: string,
) {
  const page = await getEntriesByPageType(
    {
      contentGroup: targetPageGroup,
      contentType: CONTENT_TYPE_ACCOUNT_PAGE.ACCOUNT_PAGE,
      locale,
    },
    CONTENT_TYPE_ACCOUNT_PAGE,
    false,
  );

  const queriedPages = page?.[0].items;
  const targetPage = queriedPages?.[0] as TypeAccountPage;
  return filterContentfulObject(targetPage);
}

/*
This function retrieves by ID one or more key-value list content entries from Contentful.

The content entries retrieved from Contentful initially look like this:

{
  items: [
    {
      fields: {
        id: 'inventoryStatuses',
        keyValues: [
          { key: 'outOfStock', value: `This product is out of stock.`}
          { key: 'backOrder', value: 'This product is on backorder.'}
          { key: 'preSale', value: 'Pre-order this product now.'}
        ]
      }
    },
    {
      fields: {
        id: 'accountSettingLabels',
        keyValues: [
          { key: 'firstName', value: 'Enter your first name'}
          { key: 'lastName', value: 'Enter your last name'}
        ]
      }
    }
  ]
}

This function converts them to the following format:

{
  inventoryStatuses: {
    outOfStock: `This product is out of stock.`,
    backOrder: 'This product is on backorder.',
    preSale: 'Pre-order this product now.'
  },
  accountSettingLabels: {
    firstName: 'Enter your first name',
    lastName: 'Enter your last name'
  }
}
*/
export async function getKeyValueLists(
  ids: string[],
  locale?: string,
  isPreview?: boolean,
): Promise<object | null> {
  try {
    const contentQuery = {
      'fields.id[in]': ids.join(',') || '',
      content_type: 'keyValueList',
      locale: locale || 'en-US',
    };

    const entries: EntryCollection<TypeKeyValueListFields> = isPreview
      ? await previewClient().getEntries(contentQuery)
      : await client.getEntries(contentQuery);

    if (!entries) {
      return null;
    }

    // There is more than likely a more performant and elegant way to achieve this using, for example, the .reduce() method.
    const mappedKeyValues: any = {};
    for (let i = 0; i < ids.length; i++) {
      const iteratedId = ids[i];
      mappedKeyValues[iteratedId] = null; // We initialize a null property for this ID.
      for (let j = 0; j < entries.items.length; j++) {
        const iteratedEntryFields = entries.items[j]?.fields;
        if (iteratedEntryFields?.id === iteratedId) {
          if (!mappedKeyValues[iteratedId]) {
            mappedKeyValues[iteratedId] = {}; // Since we have a match, make the property a nested object.
          }
          for (let k = 0; k < iteratedEntryFields?.keyValues.length; k++) {
            const keyValue = iteratedEntryFields?.keyValues[k];
            mappedKeyValues[iteratedId][keyValue.key] = keyValue.value;
          }
        }
      }
    }

    return mappedKeyValues;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function getInventoryStatusCopy(
  locale?: string,
  isPreview?: boolean,
) {
  const inventoryStatusCopy = (await getKeyValueLists(
    ['inventoryStatuses'],
    locale,
    isPreview,
  )) as any;
  return (inventoryStatusCopy?.inventoryStatuses ||
    null) as InventoryStatusCopy | null;
}

export async function getProductVariantsCopy(
  locale?: string,
  isPreview?: boolean,
) {
  const productVariantsCopy = (await getKeyValueLists(
    ['productVariantTranslations'],
    locale,
    isPreview,
  )) as any;
  return productVariantsCopy?.productVariantTranslations || null;
}

export async function getProductPageGeneralCopy(
  locale?: string,
  isPreview?: boolean,
) {
  const productPageGeneralCopy = (await getKeyValueLists(
    ['productPageTranslations'],
    locale,
    isPreview,
  )) as any;
  return productPageGeneralCopy?.productPageTranslations || null;
}
